import { Field } from 'react-final-form'
import styled from '@emotion/styled'
import _ from 'lodash'
import SliderInputField from './SliderInputField'
import { BorderedCard, CardTitle, ContribLimitText } from './EditDrawerStyle'
import { numberToDollars } from '../../../utils'
import { WarningTooltip, TooltipText } from '../../../components'
import { useMemo } from 'react'

const Container = styled.div`
  padding-bottom: ${p => (p.moreFields ? '27px' : '0')};
`

const MakeChangesContribution = ({ store, editable, form, values }) => {
  const {
    account: { annualSalary },
    person: { displayName },
    spouse: { firstName, annualIncome: spouseAnnualSalary },
    primaryEmployerConfig,
    spouseEmployerConfig,
    active,
    spouseAccountName,
    special457,
    special403b,
    plan: { limits },
    additionalCatchup,
    ectPretaxLimitPct,
    ectPretaxLimit,
  } = store
  const { rothPreTaxLimitAmount, effectiveRothPreTaxLimitPercent } = limits
  const maximumAmount = additionalCatchup?.maximumAmount ?? 0

  const {
    primaryEmployer: primaryEmployerEditable,
    special457: special457Editable,
    special403b: special403bEditable,
    spouseEmployer: spouseEmployerEditable,
  } = editable

  const selfAnnualCompLimitApplied = annualSalary > limits.annualContributionLimit
  const spouseAnnualCompLimitApplied = spouseAnnualSalary > limits.annualContributionLimit

  const atLeastOneSectionIsAvailable =
    primaryEmployerEditable || special457Editable || special403bEditable || spouseEmployerEditable

  const brand = _.get(store, 'store.config.template.brandName', 'GuidedChoice')

  const getContribAppearsHighThreshold = spouse => {
    if (spouse) {
      return ectPretaxLimit
    }
    return rothPreTaxLimitAmount
  }

  const getMaxSliderValue = spouse => {
    if (spouse) {
      return ectPretaxLimitPct
    }
    return effectiveRothPreTaxLimitPercent
  }

  const primaryEmployerOverviewInput = useMemo(() => {
    switch (active.planType) {
      case 2:
        // 403(b)
        return numberToDollars(Math.round(values.primary403bPreTaxDollars), true)
      case 4:
        // 457
        return numberToDollars(
          Math.round(values.primary457PreTaxDollars + values.primary457RothDollars),
          true
        )
      default:
        // 401(k) and 401(a)
        return numberToDollars(
          Math.round(
            values.primaryPreTaxDollars + values.primaryRothDollars + values.primaryAfterTaxDollars
          ),
          true
        )
    }
  }, [
    active.planType,
    values.primary403bPreTaxDollars,
    values.primary457PreTaxDollars,
    values.primary457RothDollars,
    values.primaryAfterTaxDollars,
    values.primaryPreTaxDollars,
    values.primaryRothDollars,
  ])

  const primaryEmployerOverviewPercent = useMemo(() => {
    switch (active.planType) {
      case 2:
        // 403(b)
        return values.primary403bPreTaxPercent
      case 4:
        // 457
        return values.primary457PreTaxPercent + values.primary457RothPercent
      default:
        // 401(k) and 401(a)
        return (
          values.primaryPreTaxPercent + values.primaryRothPercent + values.primaryAfterTaxPercent
        )
    }
  }, [
    active.planType,
    values.primary403bPreTaxPercent,
    values.primary457PreTaxPercent,
    values.primary457RothPercent,
    values.primaryAfterTaxPercent,
    values.primaryPreTaxPercent,
    values.primaryRothPercent,
  ])

  const primaryEmployerContributionsTotal = useMemo(() => {
    switch (active.planType) {
      case 2:
        // 403(b)
        return Math.round(values.primary403bPreTaxDollars)
      case 4:
        // 457
        return Math.round(values.primary457PreTaxDollars + values.primary457RothDollars)
      default:
        // 401(k) and 401(a)
        return Math.round(
          values.primaryPreTaxDollars + values.primaryRothDollars + values.primaryAfterTaxDollars
        )
    }
  }, [
    active.planType,
    values.primary403bPreTaxDollars,
    values.primary457PreTaxDollars,
    values.primary457RothDollars,
    values.primaryAfterTaxDollars,
    values.primaryPreTaxDollars,
    values.primaryRothDollars,
  ])

  const primaryEmployerPreTax = useMemo(() => {
    switch (active.planType) {
      case 2:
        // 403(b)
        return Math.round(values.primary403bPreTaxDollars)
      case 4:
        // 457
        return Math.round(values.primary457PreTaxDollars)
      default:
        // 401(k) and 401(a)
        return Math.round(values.primaryPreTaxDollars)
    }
  }, [
    active.planType,
    values.primary403bPreTaxDollars,
    values.primary457PreTaxDollars,
    values.primaryPreTaxDollars,
  ])

  const primaryEmployerRoth = useMemo(() => {
    switch (active.planType) {
      case 2:
        // 403(b)
        // No roth for 403b
        return 0
      case 4:
        // 457
        return Math.round(values.primary457RothDollars)
      default:
        // 401(k) and 401(a)
        return Math.round(values.primaryRothDollars)
    }
  }, [active.planType, values.primary457RothDollars, values.primaryRothDollars])

  const onChangePrimaryEmployerPreTax = value => {
    switch (active.planType) {
      case 2:
        // 403(b)
        form.batch(() => {
          form.change('primary403bPreTaxPercent', value)
          form.change('primary403bPreTaxDollars', Math.round((value * annualSalary) / 100) || 0)
        })
        break
      case 4:
        // 457
        form.batch(() => {
          form.change('primary457PreTaxPercent', value)
          form.change('primary457PreTaxDollars', Math.round((value * annualSalary) / 100) || 0)
        })
        break
      default:
        // 401(k) and 401(a)
        form.batch(() => {
          form.change('primaryPreTaxPercent', value)
          form.change('primaryPreTaxDollars', Math.round((value * annualSalary) / 100) || 0)
        })
        break
    }
  }

  const onChangePrimaryEmployerRoth = value => {
    switch (active.planType) {
      case 2:
        // 403(b)
        // no-op, no roth for 403b, shouldnt ever happen
        break
      case 4:
        // 457
        form.batch(() => {
          form.change('primary457RothPercent', value)
          form.change('primary457RothDollars', Math.round((value * annualSalary) / 100) || 0)
        })
        break
      default:
        // 401(k) and 401(a)
        form.batch(() => {
          form.change('primaryRothPercent', value)
          form.change('primaryRothDollars', Math.round((value * annualSalary) / 100) || 0)
        })
        break
    }
  }

  const primaryEmployerPreTaxFieldname = useMemo(() => {
    switch (active.planType) {
      case 2:
        // 403(b)
        return 'primary403bPreTaxPercent'
      case 4:
        // 457
        return 'primary457PreTaxPercent'
      default:
        // 401(k) and 401(a)
        return 'primaryPreTaxPercent'
    }
  }, [active.planType])
  const primaryEmployerRothFieldname = useMemo(() => {
    switch (active.planType) {
      case 4:
        // 457
        return 'primary457RothPercent'
      default:
        // 401(k) and 401(a)
        return 'primaryRothPercent'
    }
  }, [active.planType])

  function primaryEmployerSection() {
    return (
      primaryEmployerEditable && (
        <Container moreFields={special457Editable || special403bEditable || spouseEmployerEditable}>
          <ContribLimitText>
            Contribution amounts shown are annualized and rounded up to the next whole percentage
            equivalent. The actual contribution total (and what {brand} uses when projecting
            results) will be limited to IRS and plan rule maximums.
          </ContribLimitText>
          <ContribLimitText>
            Your plan does not allow you to contribute more than {effectiveRothPreTaxLimitPercent}%
            of your paycheck.
          </ContribLimitText>
          <CardTitle>
            {displayName ? `${displayName}'s` : ''} {active.name || 'employer plan account '}{' '}
            contributions
          </CardTitle>

          <SliderInputField
            readonly
            percentContribLimitExceededKey='primaryEmployerAccount'
            inputValue={primaryEmployerOverviewInput}
            percentValue={primaryEmployerOverviewPercent}
            onChange={() => {}}
            percentLabel={`of ${displayName}'s income`}
            inputLabel='Annual total'
            limits={{ ...limits, maximumAmount: maximumAmount }}
            displayAnnualCompLimitHelp={selfAnnualCompLimitApplied}
            displayContribAppearsHighHelp={
              getContribAppearsHighThreshold() < primaryEmployerContributionsTotal
            }
            displayPercentContribLimitExceeded={
              getMaxSliderValue(false) < primaryEmployerOverviewPercent
            }
            maxValue={getMaxSliderValue(false)}
          />

          <Field
            name={primaryEmployerPreTaxFieldname}
            format={v => v}
            parse={v => v}
            render={({ input }) => (
              <SliderInputField
                inputValue={primaryEmployerPreTax}
                percentValue={input.value || 0}
                onChange={onChangePrimaryEmployerPreTax}
                percentLabel='of income'
                inputLabel='Pre-tax annual contribution'
                maxValue={getMaxSliderValue(false)}
              />
            )}
          />

          {primaryEmployerConfig.rothContribAllowed && (
            <Field
              name={primaryEmployerRothFieldname}
              format={v => v}
              parse={v => v}
              render={({ input }) => (
                <SliderInputField
                  inputValue={primaryEmployerRoth}
                  percentValue={input.value || 0}
                  onChange={onChangePrimaryEmployerRoth}
                  percentLabel='of income'
                  inputLabel='Roth annual contribution'
                  maxValue={getMaxSliderValue(false)}
                />
              )}
            />
          )}

          {primaryEmployerConfig.posttaxContribAllowed && (
            <Field
              name='primaryAfterTaxPercent'
              format={v => v}
              parse={v => v}
              render={({ input }) => (
                <SliderInputField
                  inputValue={Math.round(values.primaryAfterTaxDollars)}
                  percentValue={input.value || 0}
                  onChange={value => {
                    form.batch(() => {
                      form.change('primaryAfterTaxPercent', value)
                      form.change(
                        'primaryAfterTaxDollars',
                        Math.round((value * annualSalary) / 100) || 0
                      )
                    })
                  }}
                  percentLabel='of income'
                  inputLabel='After-tax annual contribution'
                  warning={
                    <WarningTooltip
                      warningLabel={'Caution'}
                      tooltip={TooltipText.cautionAboutAfterTaxContributions()}
                    />
                  }
                  maxValue={getMaxSliderValue(false)}
                />
              )}
            />
          )}
        </Container>
      )
    )
  }

  function special457Section() {
    return (
      special457Editable && (
        <Container moreFields={special403bEditable || spouseEmployerEditable}>
          <CardTitle>
            {`${displayName ? `${displayName}'s` : ''} ${
              special457.name ? special457.name : 'employer plan account '
            } contributions`}
          </CardTitle>

          <SliderInputField
            readonly
            percentContribLimitExceededKey='special457Account'
            inputValue={numberToDollars(
              Math.round(values.primary457PreTaxDollars + values.primary457RothDollars) || 0,
              true
            )}
            percentValue={values.primary457PreTaxPercent + values.primary457RothPercent || 0}
            onChange={() => {}}
            percentLabel={`of ${displayName}'s income`}
            inputLabel='Annual total'
            limits={{ ...limits, maximumAmount: maximumAmount }}
            displayAnnualCompLimitHelp={selfAnnualCompLimitApplied}
            displayContribAppearsHighHelp={
              getContribAppearsHighThreshold() < Math.round(values.primary457PreTaxDollars)
            }
            displayPercentContribLimitExceeded={
              getMaxSliderValue(false) <
              Math.round(values.primary457PreTaxPercent + values.primary457RothPercent)
            }
            maxValue={getMaxSliderValue(false)}
          />

          <Field
            name='primary457PreTaxPercent'
            format={v => v}
            parse={v => v}
            render={({ input }) => (
              <SliderInputField
                inputValue={Math.round(values.primary457PreTaxDollars)}
                percentValue={input.value || 0}
                onChange={value => {
                  form.batch(() => {
                    form.change('primary457PreTaxPercent', value)
                    form.change(
                      'primary457PreTaxDollars',
                      Math.round((value * annualSalary) / 100) || 0
                    )
                  })
                }}
                percentLabel='of income'
                inputLabel='Pre-tax annual contribution'
                maxValue={getMaxSliderValue(false)}
              />
            )}
          />
        </Container>
      )
    )
  }

  function special403bSection() {
    return (
      special403bEditable && (
        <Container moreFields={spouseEmployerEditable}>
          <CardTitle>
            {`${displayName ? `${displayName}'s` : ''} ${
              special403b.name ? special403b.name : 'employer plan account '
            } contributions`}
          </CardTitle>

          <SliderInputField
            readonly
            percentContribLimitExceededKey='special403bAccount'
            inputValue={numberToDollars(Math.round(values.primary403bPreTaxDollars) || 0, true)}
            percentValue={values.primary403bPreTaxPercent}
            onChange={() => {}}
            percentLabel={`of ${displayName}'s income`}
            inputLabel='Annual total'
            limits={{ ...limits, maximumAmount: maximumAmount }}
            displayAnnualCompLimitHelp={selfAnnualCompLimitApplied}
            displayContribAppearsHighHelp={
              getContribAppearsHighThreshold() < Math.round(values.primary403bPreTaxDollars)
            }
            displayPercentContribLimitExceeded={
              getMaxSliderValue(false) < Math.round(values.primary403bPreTaxPercent)
            }
            maxValue={getMaxSliderValue(false)}
          />

          <Field
            name='primary403bPreTaxPercent'
            format={v => v}
            parse={v => v}
            render={({ input }) => (
              <SliderInputField
                inputValue={Math.round(values.primary403bPreTaxDollars)}
                percentValue={input.value || 0}
                onChange={value => {
                  form.batch(() => {
                    form.change('primary403bPreTaxPercent', value)
                    form.change(
                      'primary403bPreTaxDollars',
                      Math.round((value * annualSalary) / 100) || 0
                    )
                  })
                }}
                percentLabel='of income'
                inputLabel='Pre-tax annual contribution'
                maxValue={getMaxSliderValue(false)}
              />
            )}
          />
        </Container>
      )
    )
  }

  function spouseSection() {
    return (
      spouseEmployerEditable && (
        <div>
          <ContribLimitText>
            For spouse plan: Contributions are limited to {ectPretaxLimitPct}%.
          </ContribLimitText>
          <CardTitle>
            {firstName ? `${firstName}'s` : ''} {spouseAccountName} contributions
          </CardTitle>

          <SliderInputField
            readonly
            percentContribLimitExceededKey='spouseEmployerAccount'
            inputValue={numberToDollars(
              Math.round(
                values.spousePreTaxDollars + values.spouseRothDollars + values.spouseAfterTaxDollars
              ) || 0,
              true
            )}
            percentValue={
              values.spousePreTaxPercent + values.spouseRothPercent + values.spouseAfterTaxPercent
            }
            onChange={() => {}}
            percentLabel={`of ${firstName}'s income`}
            inputLabel='Annual total'
            limits={{ ...limits, maximumAmount: maximumAmount }}
            displayAnnualCompLimitHelp={spouseAnnualCompLimitApplied}
            displayContribAppearsHighHelp={
              getContribAppearsHighThreshold(true) <
              Math.round(
                values.spousePreTaxDollars + values.spouseRothDollars + values.spouseAfterTaxDollars
              )
            }
            displayPercentContribLimitExceeded={
              getMaxSliderValue(true) <
              Math.round(
                values.spousePreTaxPercent + values.spouseRothPercent + values.spouseAfterTaxPercent
              )
            }
            maxValue={getMaxSliderValue(true)}
          />

          <Field
            name='spousePreTaxPercent'
            format={v => v}
            parse={v => v}
            render={({ input }) => (
              <SliderInputField
                inputValue={Math.round(values.spousePreTaxDollars)}
                percentValue={input.value || 0}
                onChange={value => {
                  form.batch(() => {
                    form.change('spousePreTaxPercent', value)
                    form.change(
                      'spousePreTaxDollars',
                      Math.round((value * spouseAnnualSalary) / 100) || 0
                    )
                  })
                }}
                percentLabel='of income'
                inputLabel='Pre-tax annual contribution'
                maxValue={getMaxSliderValue(true)}
              />
            )}
          />

          {spouseEmployerConfig.rothContribAllowed && (
            <Field
              name='spouseRothPercent'
              format={v => v}
              parse={v => v}
              render={({ input }) => (
                <SliderInputField
                  inputValue={Math.round(values.spouseRothDollars)}
                  percentValue={input.value || 0}
                  onChange={value => {
                    form.batch(() => {
                      form.change('spouseRothPercent', value)
                      form.change(
                        'spouseRothDollars',
                        Math.round((value * spouseAnnualSalary) / 100) || 0
                      )
                    })
                  }}
                  percentLabel='of income'
                  inputLabel='Roth annual contribution'
                  maxValue={getMaxSliderValue(true)}
                />
              )}
            />
          )}

          {spouseEmployerConfig.posttaxContribAllowed && (
            <Field
              name='spouseAfterTaxPercent'
              format={v => v}
              parse={v => v}
              render={({ input }) => (
                <SliderInputField
                  inputValue={Math.round(values.spouseAfterTaxDollars)}
                  percentValue={input.value || 0}
                  onChange={value => {
                    form.batch(() => {
                      form.change('spouseAfterTaxPercent', value)
                      form.change(
                        'spouseAfterTaxDollars',
                        Math.round((value * spouseAnnualSalary) / 100) || 0
                      )
                    })
                  }}
                  percentLabel='of income'
                  inputLabel='After-tax annual contribution'
                  warning={
                    <WarningTooltip
                      warningLabel={'Caution'}
                      tooltip={TooltipText.cautionAboutAfterTaxContributions()}
                    />
                  }
                  maxValue={getMaxSliderValue(true)}
                />
              )}
            />
          )}
        </div>
      )
    )
  }

  return (
    atLeastOneSectionIsAvailable && (
      <BorderedCard>
        {primaryEmployerSection()}

        {special457Section()}

        {special403bSection()}

        {spouseSection()}
      </BorderedCard>
    )
  )
}

export default MakeChangesContribution
